import { Plus, Trash2 } from "lucide-react";
import { TicketQuestion } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Reusable editor for a list of modal text-input questions. Discord shows max 5 inputs
 * per modal, so callers whose bot-side handler shows a single modal (no continuation)
 * must pass `maxQuestions={5}` — anything past that would silently never be asked.
 * Leave `maxQuestions` unset for a flow that pages through more in "Part 2", "Part 3"...
 * modals (e.g. Whitelist), where there's no real ceiling on how many you can add.
 * Shared across Tickets (direct form / per-option form) and Whitelist (per-language form).
 */
export function QuestionListEditor({
  questions,
  onChange,
  maxQuestions,
}: {
  questions: TicketQuestion[];
  onChange: (next: TicketQuestion[]) => void;
  maxQuestions?: number;
}) {
  const updateQuestion = (qIdx: number, patch: Partial<TicketQuestion>) => {
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {questions.map((q, qIdx) => {
        const labelTooLong = q.label.length > 45;
        return (
        <div key={q.id || qIdx} className="rounded-lg border border-border p-3 space-y-2 bg-muted/20">
          <div className="flex gap-2 items-center">
            <Input
              className={`text-base sm:text-sm ${labelTooLong ? "border-amber-500/60 focus-visible:ring-amber-500/40" : ""}`}
              value={q.label}
              placeholder="Question label"
              onChange={(e) => updateQuestion(qIdx, { label: e.target.value })}
            />
            <Button
              size="icon"
              variant="ghost"
              className="shrink-0"
              onClick={() => onChange(questions.filter((_, i) => i !== qIdx))}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
          {labelTooLong && (
            <p className="text-[11px] text-amber-500">
              ⚠ Discord only shows the first 45 characters in the applicant's form (currently {q.label.length}) — the rest
              will be cut off there. {q.placeholder?.trim() ? "Your placeholder text will still show the full question." : "Add a placeholder below to still show the full question as hint text."}
            </p>
          )}
          <div className="grid sm:grid-cols-4 gap-2">
            <Select
              value={q.style}
              onValueChange={(v) => updateQuestion(qIdx, { style: v as "short" | "paragraph" })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Short</SelectItem>
                <SelectItem value="paragraph">Long</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={1}
              max={1500}
              className="text-base sm:text-sm"
              value={q.max_length}
              onChange={(e) => updateQuestion(qIdx, { max_length: Math.max(1, Number(e.target.value) || 200) })}
            />
            <Input
              className="text-base sm:text-sm"
              value={q.placeholder ?? ""}
              placeholder={labelTooLong ? "Full question (shown as hint text)" : "Placeholder"}
              onChange={(e) => updateQuestion(qIdx, { placeholder: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-md px-3">
              <input
                type="checkbox"
                checked={q.required}
                onChange={(e) => updateQuestion(qIdx, { required: e.target.checked })}
              />
              Required
            </label>
          </div>
        </div>
        );
      })}
      <Button
        size="sm"
        variant="outline"
        disabled={maxQuestions != null && questions.length >= maxQuestions}
        onClick={() =>
          onChange([
            ...questions,
            {
              id: `q_${Date.now()}`,
              label: "New question",
              style: "short",
              required: true,
              max_length: 200,
              placeholder: "",
            },
          ])
        }
        className="w-full sm:w-auto"
      >
        <Plus className="h-4 w-4" />
        Add question {questions.length > 0 ? (maxQuestions != null ? `(${questions.length}/${maxQuestions})` : `(${questions.length})`) : ""}
      </Button>
    </div>
  );
}
